'use client'
import { useState } from "react";
import { RiShareLine } from 'react-icons/ri'
import tailwindConfig from '@tailwind.config'

const SHARE_DICTIONARY = {
    en: { share: "Share" },
    th: { share: "แชร์" },
    cn: { share: "分享" },
    ru: { share: "Поделиться" },
    de: { share: "Teilen" },
    fr: { share: "Partager" },
    it: { share: "Condividi" },
    es: { share: "Compartir" },
    ja: { share: "シェア" },
    ko: { share: "공유" },
    tr: { share: "Paylaş" },
    he: { share: "שתף" },
    ar: { share: "مشاركة" },
};

const RTL_LANGS = new Set(["he", "ar"]);

const ShareButton = ({ title, text, url, cover, dictionary, lang = "en" }) => {
    const [isSharing, setIsSharing] = useState(false);
    const [shareStatus, setShareStatus] = useState(null); // 'success' | 'error' | null

    const resolvedDict = dictionary ?? SHARE_DICTIONARY[lang] ?? SHARE_DICTIONARY.en;
    const label = resolvedDict?.property?.share ?? resolvedDict?.share ?? SHARE_DICTIONARY.en.share;
    const isRTL = RTL_LANGS.has(lang);

    const handleClick = async () => {
        if (isSharing) return;
        setIsSharing(true);
        setShareStatus(null);

        try {
            const shareData = {
                title: title,
                text: text,
                url: `/${url}`,
            };

            if (!window.navigator.share) {
                throw new Error("Web Share API not supported");
            }

            await window.navigator.share(shareData);
            setShareStatus("success");
        } catch (error) {
            // AbortError = user dismissed — not a real error
            if (error?.name !== "AbortError") {
                setShareStatus("error");
            }
        } finally {
            setIsSharing(false);
        }
    };

    return (
        <button
            onClick={handleClick}
            disabled={isSharing}
            type="button"
            aria-label={label}
            aria-busy={isSharing}
            aria-live="polite"
            dir={isRTL ? "rtl" : "ltr"}
            title={label}
        >
            <div className="flex flex-col items-center" aria-hidden="true">
                <RiShareLine
                    color={tailwindConfig.theme.extend.colors.secondary}
                    size={28}
                    aria-hidden="true"
                    focusable="false"
                />
                <span className="text-maincolor">{label}</span>
            </div>

            {/* Screen-reader-only live status announcement */}
            <span className="sr-only" role="status" aria-live="polite">
                {shareStatus === "success"
                    ? (lang === "ar" ? "تمت المشاركة" : lang === "he" ? "שותף בהצלחה" : "Shared successfully")
                    : shareStatus === "error"
                    ? (lang === "ar" ? "فشلت المشاركة" : lang === "he" ? "שגיאה בשיתוף" : "Share failed")
                    : ""}
            </span>
        </button>
    );
};

export default ShareButton;